import { useState } from 'react';

import { NotificationRule } from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { Button } from '@/components/ui/button';
import {
  Credenza,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { useUnsubscribeNotificationRule } from '@/features/notification-rules/hooks/mutations/use-unsubscribe-notification-rule.ts';
import { LoaderIcon } from 'lucide-react';

export function NotificationRulesTableRowActionUnsubscribeDialog({
  notificationRule,
}: {
  notificationRule: NotificationRule;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutate: unsubscribe, isPending } = useUnsubscribeNotificationRule({
    onSuccess: () => {
      setIsDialogOpen(false);
    },
  });

  const onClickUnsubscribe = () => {
    unsubscribe({ ruleId: notificationRule.notificationRuleId });
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <button className="focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors select-none hover:bg-gray-100 data-disabled:pointer-events-none data-disabled:opacity-50">
          <span>Unsubscribe</span>
        </button>
      </CredenzaTrigger>
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>Unsubscribe from Notification Rule</CredenzaTitle>
          <CredenzaDescription>
            Are you sure you want to unsubscribe from{' '}
            <span className="font-medium">{notificationRule.notificationTitle}</span>? You will
            no longer receive notifications from this company rule.
          </CredenzaDescription>
        </CredenzaHeader>
        <CredenzaFooter className="gap-2 sm:space-x-0">
          <CredenzaClose asChild>
            <Button variant="outline">Cancel</Button>
          </CredenzaClose>
          <Button variant="destructive" onClick={onClickUnsubscribe} disabled={isPending}>
            {isPending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Unsubscribe
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
}
